/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useCallback, useId, useMemo } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@/hooks/use-navigate';

import type { ShopperSearch } from '@/scapi';

import { NativeSelect } from '@/components/ui/native-select';
import { PRODUCT_SEARCH_QUERY_PARAMS } from '@/lib/query-params';
import { useBrand } from '@/lib/brand/use-brand';
import { SORT_OPTIONS_BY_BRAND, resolveSortOptions, selectSortOption } from '@/lib/search/sort-options';

/**
 * CategorySorting Component
 *
 * Renders a dropdown select control that allows users to sort product search results
 * by different criteria (e.g., brand, price, name, etc.). The component updates
 * the URL parameters when a new sorting option is selected, triggering a page navigation
 * to refresh the results with the new sort order.
 *
 * @param props - Component props
 * @param props.result - Product search result object from Salesforce Commerce Cloud
 * @param props.result.sortingOptions - Array of available sorting options with id and label
 * @param props.result.selectedSortingOption - Currently selected sorting option ID
 *
 * @returns A select dropdown for sorting options, or null if no sorting options are available
 *
 * @example
 * ```tsx
 * <CategorySorting result={productSearchResult} />
 * ```
 *
 * Features:
 * - Uses native select component with consistent UI styling
 * - Generates unique IDs to support multiple instances on the same page
 * - Automatically updates URL parameters (sort, offset) when selection changes
 * - Accessible with proper label-select association
 */
export default function CategorySorting({
    result,
}: {
    result: ShopperSearch.schemas['ProductSearchResult'];
}): ReactElement | null {
    const navigate = useNavigate();
    const location = useLocation();
    const navigation = useNavigation();
    const { t } = useTranslation('category');
    // The option LABELS live in the `search` namespace alongside the curated
    // lists that define them (`sort.bestMatch` &c.); only this control's own
    // "Sort by:" label is a `category` key. Two hooks rather than a combined
    // namespace array so each `t` stays typed to the keys it actually reads.
    const { t: tSearch } = useTranslation('search');
    const brand = useBrand();
    const isPending = navigation.state !== 'idle';
    const selectId = useId();

    const serverOptions = useMemo(() => result?.sortingOptions || [], [result?.sortingOptions]);

    /**
     * WHY THIS CONTROL RESOLVES ITS SELECTION THROUGH `selectSortOption`.
     *
     * It used to hand `result.selectedSortingOption` straight to the <select>.
     * SCAPI leaves that field EMPTY when the request carried no `sort`, so the
     * value matched no <option> and the browser fell back to index 0 — which,
     * with Business Manager now returning sorting options, is
     * `price-low-to-high`. Measured on the dev server 2026-08-22 at 1280 on
     * `/BergdorfGoodman/category/womens-clothing`: `selectedIndex` 0, label
     * "Price: low to high", over a grid in DEFAULT order. The control named a
     * sort the results were not in — the same defect `SortSelect` carried, by a
     * different mechanism (an empty server field rather than an `options[0]`
     * fallback in our own code).
     *
     * `resolveSortOptions` carries the curated list's `isDefault` across onto
     * the server list by id match — the flag SCAPI does not expose — and
     * `selectSortOption` then resolves both empty-lookup cases (no `?sort=`,
     * and a `?sort=` this brand's list does not contain) onto that default row.
     * Server labels and server order are untouched: they are real BM
     * configuration and still win.
     */
    const sortingOptions = useMemo(
        () => resolveSortOptions(serverOptions, SORT_OPTIONS_BY_BRAND[brand]),
        [serverOptions, brand]
    );

    /**
     * The sort id to display, most authoritative first.
     *
     * While a navigation is pending, `navigation.location` holds the target, so
     * the intended sort reads immediately (optimistic). Otherwise the current
     * URL is the request the loader actually served, and `selectedSortingOption`
     * is SCAPI's own echo of it — kept as the last real signal before the
     * default, since it is the only one that survives a sort applied by
     * something other than this control.
     */
    const requestedSort =
        (navigation.location
            ? new URLSearchParams(navigation.location.search).get(PRODUCT_SEARCH_QUERY_PARAMS.SORT)
            : new URLSearchParams(location.search).get(PRODUCT_SEARCH_QUERY_PARAMS.SORT)) ||
        result.selectedSortingOption ||
        undefined;

    const effectiveSortingOption = selectSortOption(sortingOptions, requestedSort)?.id;

    const navigatePage = useCallback(
        (sort: string) => {
            const params = new URLSearchParams(location.search);
            params.set(PRODUCT_SEARCH_QUERY_PARAMS.SORT, sort);
            params.set(PRODUCT_SEARCH_QUERY_PARAMS.OFFSET, '0');
            void navigate({
                ...location,
                search: `?${params.toString()}`,
            });
        },
        [location, navigate]
    );

    // Return null if no sorting options available.
    //
    // Deliberately gated on the SERVER list, not the resolved one: resolving
    // falls back to the brand's curated list, which is never empty, so gating
    // on `sortingOptions` here would make this control appear on a result that
    // previously rendered nothing. Both mounting routes already gate on
    // `sortingOptions.length > 0` too — this keeps the component's own contract
    // identical to what those gates assume.
    if (serverOptions.length === 0) {
        return null;
    }

    return (
        <div
            className={`flex items-center space-x-2${isPending ? ' pointer-events-none opacity-50 transition-opacity' : ''}`}>
            <label htmlFor={selectId} className="type-label-medium whitespace-nowrap text-muted-foreground">
                {t('sortBy', { defaultValue: 'Sort by:' })}
            </label>
            <NativeSelect
                id={selectId}
                className="type-label-medium"
                data-testid="category-sort-select"
                value={effectiveSortingOption || ''}
                onChange={(e) => void navigatePage(e.target.value)}>
                {sortingOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                        {/* Server label when BM supplied one; otherwise the curated
                            entry's `search`-namespace key with live's verbatim
                            string as the defaultValue. */}
                        {option.label ??
                            (option.labelKey
                                ? tSearch(option.labelKey, { defaultValue: option.defaultLabel ?? option.id })
                                : option.id)}
                    </option>
                ))}
            </NativeSelect>
        </div>
    );
}
